import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Switch,
  Tag,
  message,
  Calendar,
  Badge,
  Typography,
  Spin,
  Result,
} from "antd";
import {
  EyeOutlined,
  EditOutlined,
  TeamOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import moment from "moment";

import "./index.css";

import { DateFormat, getApi, saveApi } from "../../helpers";
import { canWrite } from "../../helpers/permissions";
import {
  opsApiCall,
  getOpsToken,
  clearOpsAuth,
  isTokenValid,
} from "../../helpers/rapsapOpsAuth";
import CardContainer from "../../components/Card";
import TableContainer from "../../components/Table";
import Sidebar from "../../components/Sidebar";
import { StoreContext } from "../../context";
import StaffForm from "./StaffForm";
import StaffDetails from "./StaffDetails";
import EditStaffPopup from "./EditStaffPopup";
import "./EditStaffPopup.css";

export default function Staff() {
  const { storeId } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [total, setTotal] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [savedPayload, setSavedPayload] = useState({});
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [attendanceSidebarVisible, setAttendanceSidebarVisible] =
    useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [selectedStaffForAttendance, setSelectedStaffForAttendance] =
    useState(null);
  const [detailsSidebarVisible, setDetailsSidebarVisible] = useState(false);
  const [editPopupVisible, setEditPopupVisible] = useState(false);
  const [staffForDetails, setStaffForDetails] = useState(null);
  const [staffForEdit, setStaffForEdit] = useState(null);

  // Attendance Viewer Component
  const AttendanceViewer = ({ staff, attendanceData, loading }) => {
    const [selectedDate, setSelectedDate] = useState(moment());

    // Convert attendance data to a map for easy lookup
    const attendanceMap = {};
    attendanceData.forEach((record) => {
      // Convert date format from "23-12-27" to "2023-12-27"
      const dateParts = record.date.split("-");
      const fullYear = `20${dateParts[0]}`;
      const formattedDate = `${fullYear}-${dateParts[1]}-${dateParts[2]}`;
      attendanceMap[formattedDate] = record;
    });

    // Get attendance for selected date
    const getSelectedDateAttendance = () => {
      const dateKey = selectedDate.format("YYYY-MM-DD");
      return attendanceMap[dateKey] || null;
    };

    // Custom date cell renderer for compact calendar
    const dateCellRender = (value) => {
      const dateKey = value.format("YYYY-MM-DD");
      const attendance = attendanceMap[dateKey];

      if (attendance) {
        return (
          <div style={{ position: "relative", height: "100%" }}>
            <div
              style={{
                position: "absolute",
                bottom: "2px",
                right: "2px",
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: "#6366f1",
              }}
            />
          </div>
        );
      }
      return null;
    };

    // Handle date selection
    const onDateSelect = (date) => {
      setSelectedDate(date);
    };

    // Handle month/year change
    const onPanelChange = (date) => {
      setSelectedDate(date);
    };

    const selectedAttendance = getSelectedDateAttendance();

    return (
      <div style={{ padding: "16px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <Typography.Title level={4} style={{ margin: 0, fontSize: "18px" }}>
            {staff.username}( {staff.name} )
          </Typography.Title>
        </div>

        {/* Compact Calendar */}
        <div
          className="attendance-calendar-compact"
          style={{ marginBottom: "16px" }}
        >
          <Calendar
            fullscreen={false}
            value={selectedDate}
            onSelect={onDateSelect}
            onPanelChange={onPanelChange}
            dateCellRender={dateCellRender}
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: "8px",
              backgroundColor: "#fff",
            }}
          />
        </div>

        {/* Modern Time Display */}
        <div className="modern-time-display">
          <div className="time-header">
            <Typography.Title
              level={5}
              style={{ fontSize: "16px", margin: 0, color: "#1f2937" }}
            >
              Time:
            </Typography.Title>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <span>Loading attendance data...</span>
            </div>
          ) : selectedAttendance ? (
            <div className="attendance-details">
              {/* Date Card */}
              <div className="detail-card date-card">
                <div className="detail-label">Date:</div>
                <div className="detail-value">
                  {selectedDate.format("DD MMM YYYY")}
                </div>
              </div>

              {/* Check In Card */}
              <div className="detail-card checkin-card">
                <div className="detail-label">Check In:</div>
                <div className="detail-value">
                  {selectedAttendance.check_in ? (
                    <span className="time-value">
                      {selectedAttendance.check_in}
                    </span>
                  ) : (
                    <span className="no-record">Not recorded</span>
                  )}
                </div>
              </div>

              {/* Check Out Card */}
              <div className="detail-card checkout-card">
                <div className="detail-label">Check Out:</div>
                <div className="detail-value">
                  {selectedAttendance.check_out ? (
                    <span className="time-value">
                      {selectedAttendance.check_out}
                    </span>
                  ) : (
                    <span className="no-record">Not recorded</span>
                  )}
                </div>
              </div>

              {/* Duration Card */}
              {selectedAttendance.check_in && selectedAttendance.check_out && (
                <div className="detail-card duration-card">
                  <div className="detail-label">Duration:</div>
                  <div className="detail-value">
                    <span className="duration-value">
                      {calculateDuration(
                        selectedAttendance.check_in,
                        selectedAttendance.check_out
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-attendance-state">
              <div className="no-attendance-icon">📅</div>
              <div className="no-attendance-text">
                No attendance record for
                <br />
                <strong>{selectedDate.format("DD MMM YYYY")}</strong>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Calculate duration between check-in and check-out
  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return "N/A";

    const start = moment(checkIn, "HH:mm:ss");
    const end = moment(checkOut, "HH:mm:ss");

    // Handle case where check-out is next day
    if (end.isBefore(start)) {
      end.add(1, "day");
    }

    const duration = moment.duration(end.diff(start));
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();

    return `${hours}h ${minutes}m`;
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  useEffect(() => {
    if (authReady) {
      getStaffList(savedPayload);
    }
  }, [authReady, storeId]);

  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getOpsToken();
      if (token) {
        console.log("Authentication initialized for staff screen");
        setAuthReady(true);
      } else {
        throw new Error("Failed to authenticate");
      }
    } catch (error) {
      console.error("Error initializing auth:", error);
      setError("Failed to initialize authentication. Please try again.");
      setAuthReady(false);
    } finally {
      setLoading(false);
    }
  };

  const getStaffList = async (payload = {}) => {
    try {
      setLoading(true);
      setError(null);

      let staffPayload = {
        user_type: "all",
        page: payload.page || 0,
        size: payload.size || 12,
        ...payload,
      };

      if (storeId) {
        staffPayload = { ...staffPayload, store_id: storeId };
      } else {
        delete staffPayload.store_id;
      }

      const res = await opsApiCall("/rapid/getUsers", staffPayload);

      if (Array.isArray(res?.data)) {
        // getUsers ignores page/size and returns every staff oldest-first, and
        // the table pages client-side; newest first means highest (auto-increment) id.
        setStaffList([...res.data].sort((a, b) => b.user_id - a.user_id));
        setTotal(res.meta?.total || res.data.length);
      } else {
        throw new Error(res?.msg || "No data received from server");
      }

      setSavedPayload(payload);
    } catch (error) {
      console.error("Error fetching staff list:", error);
      setError("Failed to load staff data. Please try again.");
      setStaffList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = () => {
    setSelectedStaff(null);
    setSidebarVisible(true);
  };

  const handleViewStaff = (staff) => {
    setStaffForDetails(staff);
    setDetailsSidebarVisible(true);
  };

  const handleViewAttendance = async (staff) => {
    try {
      console.log("Fetching attendance for staff:", staff);
      setAttendanceLoading(true);
      setSelectedStaffForAttendance(staff);

      // Close details sidebar and open attendance sidebar
      setDetailsSidebarVisible(false);
      setAttendanceSidebarVisible(true);

      const res = await opsApiCall("/user/getAttendance", {
        staff_id: staff.user_id.toString(),
        store_id: staff.store_id.toString(),
      });

      if (Array.isArray(res?.data)) {
        console.log("Attendance data:", res.data);
        setAttendanceData(res.data);
        message.success(`Fetched attendance for ${staff.name}`);
      } else {
        message.info("No attendance data found");
        setAttendanceData([]);
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      message.error("Failed to fetch attendance data");
      setAttendanceData([]);
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleEditStaff = (staff) => {
    setStaffForEdit(staff);
    setDetailsSidebarVisible(false);
    setEditPopupVisible(true);
  };

  const handleUpdateStaff = async () => {
    setEditPopupVisible(false);
    setStaffForEdit(null);
    await getStaffList(savedPayload);
  };

  const handleCancelEdit = () => {
    setEditPopupVisible(false);
    setStaffForEdit(null);
  };

  const handleToggleStatus = async (staff, checked) => {
    try {
      const res = await opsApiCall("/user/updateStaff", {
        key: "JWT",
        secret: "RAPSAP",
        staff_id: staff.user_id.toString(),
        is_active: checked ? 1 : 0,
      });

      if (res) {
        message.success(
          checked
            ? "Staff Activated Successfully"
            : "Staff Deactivated Successfully"
        );
        // Update the local state
        const updatedStaffList = staffList.map((s) =>
          s.user_id === staff.user_id
            ? { ...s, is_active: checked ? 1 : 0 }
            : s
        );
        setStaffList(updatedStaffList);

        // Update details view if open
        if (staffForDetails && staffForDetails.user_id === staff.user_id) {
          setStaffForDetails({ ...staffForDetails, is_active: checked ? 1 : 0 });
        }
      }
    } catch (error) {
      console.error("Error updating staff status:", error);
      message.error("Failed to update staff status");
    }
  };

  const handleSaveStaff = async (staffData) => {
    try {
      if (selectedStaff) {
        // Update existing staff
        const updatePayload = {
          user_id: selectedStaff.user_id,
          store_id: staffData.store_id,
          username: staffData.username,
          staff_name: staffData.staff_name,
          staff_mobile: staffData.staff_mobile,
          staff_type: staffData.staff_type,
        };

        console.log("Updating staff with payload:", updatePayload);
        const res = await opsApiCall("/user/updateStaff", updatePayload);
        if (res) {
          message.success("Staff Updated Successfully");
          getStaffList(savedPayload); // Refresh the list
        }
      } else {
        // Create new staff with exact payload format
        const createPayload = {
          store_id: staffData.store_id,
          username: staffData.username,
          staff_name: staffData.staff_name,
          password: staffData.password || "123456", // Default password if not provided
          staff_mobile: staffData.staff_mobile,
          staff_type: staffData.staff_type,
          // Operations staff only (set by StaffForm): worker -> role_ids, store manager -> role_id.
          ...(staffData.access_level && { access_level: staffData.access_level }),
          ...(staffData.role_ids && { role_ids: staffData.role_ids }),
          ...(staffData.role_id && { role_id: staffData.role_id }),
        };

        console.log("Creating staff with payload:", createPayload);
        const res = await opsApiCall("/user/createStaff", createPayload);
        if (!res?.success) throw new Error(res?.msg || "Failed to create staff");
        getStaffList(savedPayload); // Refresh the list
      }
      setSidebarVisible(false);
      setSelectedStaff(null);
    } catch (error) {
      console.error("Error saving staff:", error);
      // StaffForm shows the message and keeps the form open.
      throw error;
    }
  };

  // Define columns inside component to access handler functions
  const columns = [
    // {
    //   title: "Edit",
    //   key: "edit",
    //   align: "center",
    //   render: (text, record) => (
    //     <Button
    //       type="link"
    //       icon={<EditOutlined />}
    //       title="To open edit user details"
    //       onClick={() => handleEditStaff(record)}
    //     />
    //   ),
    // },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Staff Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
    },
    {
      title: "Staff Mobile",
      dataIndex: "mobile",
      key: "mobile",
      sorter: true,
    },

    {
      title: "Store Name",
      dataIndex: "store_name",
      key: "store_name",
      sorter: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => (
        <Tag
          color={
            text === "admin"
              ? "red"
              : text === "superadmin"
              ? "purple"
              : text === "delivery"
              ? "blue"
              : "green"
          }
        >
          {text || "staff"}
        </Tag>
      ),
    },

    {
      title: "App Version",
      dataIndex: "app_version",
      key: "app_version",
      render: (text) => text || "N/A",
    },
    {
      title: "App Platform",
      dataIndex: "app_platform",
      key: "app_platform",
      render: (text) => text || "N/A",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (text, record) => (
        <Switch
          size="small"
          checked={record.is_active === 1}
          title="To update user details"
          onChange={(checked) => handleToggleStatus(record, checked)}
        />
      ),
    },
  ];

  // Error state
  if (error && !loading) {
    return (
      <CardContainer>
        <Result
          status="error"
          title="Failed to Load Staff Data"
          subTitle={error}
          extra={[
            <Button type="primary" key="retry" onClick={initializeAuth}>
              Retry
            </Button>,
          ]}
        />
      </CardContainer>
    );
  }

  // Loading state
  if (loading && staffList.length === 0) {
    return (
      <CardContainer>
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px" }}>Loading staff data...</div>
        </div>
      </CardContainer>
    );
  }

  // Success state - only render table when we have data
  return (
    <>
      <CardContainer hoverable={false}>
        <TableContainer
          breadcrumbTitle="Staff Management"
          loading={loading}
          columns={columns}
          data={staffList}
          total={total}
          searchFor="Staff Name, Mobile, Username"
          functionsHandler={getStaffList}
          rowClickHandler={handleViewStaff}
          addButton={canWrite()}
          addHandler={handleCreateStaff}
        />
      </CardContainer>

      <Sidebar
        title={selectedStaff ? "Edit Staff" : "Add New Employee"}
        visible={sidebarVisible}
        onClose={() => {
          setSidebarVisible(false);
          setSelectedStaff(null);
        }}
      >
        <StaffForm
          initialData={selectedStaff}
          onSave={handleSaveStaff}
          onCancel={() => {
            setSidebarVisible(false);
            setSelectedStaff(null);
          }}
        />
      </Sidebar>

      {/* Staff Details Sidebar */}
      <Sidebar
        title="Staff Details"
        visible={detailsSidebarVisible}
        onClose={() => {
          setDetailsSidebarVisible(false);
          setStaffForDetails(null);
        }}
      >
        {staffForDetails && (
          <StaffDetails
            staff={staffForDetails}
            onEdit={() => handleEditStaff(staffForDetails)}
            onViewAttendance={() => handleViewAttendance(staffForDetails)}
            onStatusChange={handleToggleStatus}
          />
        )}
      </Sidebar>

      {/* Edit Staff Popup */}
      <EditStaffPopup
        visible={editPopupVisible}
        initialData={staffForEdit}
        onSave={handleUpdateStaff}
        onCancel={handleCancelEdit}
      />

      {/* Attendance Sidebar */}
      <Sidebar
        title="Staff Attendance"
        visible={attendanceSidebarVisible}
        onClose={() => {
          setAttendanceSidebarVisible(false);
          setSelectedStaffForAttendance(null);
          setAttendanceData([]);
        }}
      >
        {selectedStaffForAttendance && (
          <AttendanceViewer
            staff={selectedStaffForAttendance}
            attendanceData={attendanceData}
            loading={attendanceLoading}
          />
        )}
      </Sidebar>
    </>
  );
}
